import { setUser as setSentryUser } from '@sentry/react'
import { useClient, useQuery } from '@weenat/client'
import { canUser, ops } from '@weenat/client/dist/core/access'
import { useHasPaymentIncident } from '@weenat/client/dist/core/billing'
import { useIntl } from '@weenat/wintl'
import { OrgContextProvider, useDefaultOrg, useOrgContext } from 'app/orgProvider'
import { Redirect, usePathname } from 'app/routx-router'
import { useLoginAs, useToken, useUserLanguage } from 'app/state'
import { UserContextProvider, useUserContext } from 'app/userProvider'
import { useInitTracking } from 'app/utils/analytics'
import { isNil, omitBy } from 'lodash-es'
import { default as momentTZ } from 'moment-timezone'
import { useEffect } from 'react'
import { useTheme } from 'styled-components'
import { clearAllStates } from './src/authentication/components/Logout'
import useCloseRadarOnPageChange from './src/dashboard/components/DashboardMap/Radar/hooks/useCloseRadarOnPageChange'
import ExternalConnectionsErrorMessage from './src/dashboard/components/common/ExternalConnectionsErrorMessage'
import ErrorScreen from './src/errors/ErrorScreen'
import { useIsAppAccessRestricted } from './src/hooks/useIsAppAccessRestricted'
import { useOverriddenFeatureFlags } from './src/hooks/useOverriddenFeatureFlags'
import useRefreshToken from './src/hooks/useRefreshToken'
import Banner from './src/kit/Banner'
import DowngradeWarn from './src/kit/DowngradeWarn'
import InvitNotificationPresentation from './src/kit/InvitNotificationPresentation'
import ModalOverlay from './src/kit/ModalOverlay'
import SmartText from './src/kit/SmartText'
import Text from './src/kit/Text'
import { LogoSphereDark } from './src/kit/icons/logos'
import LoadingCircle from './src/kit/loaders/LoadingCircle'

const useLocaleSync = () => {
  const { locales } = useIntl()
  const [, setUserLanguage] = useUserLanguage()

  useEffect(() => {
    momentTZ.updateLocale(locales.current, {
      week: {
        dow: 1 // First day of week is Monday
      }
    })
    setUserLanguage(locales.current)
  }, [locales.current])

  return locales.current
}

const OnUserLoginBehavior: FC = ({ children }) => {
  const org = useOrgContext()
  const user = useUserContext()
  const [token] = useToken()
  const { colors } = useTheme()

  useLocaleSync()
  useInitTracking()
  useCloseRadarOnPageChange()
  useRefreshToken()
  useOverriddenFeatureFlags()

  useEffect(() => {
    if (!isNil(user)) {
      setSentryUser(omitBy(user, (value) => isNil(value)))
    }
  }, [user])

  /** They are the same as for {@link useIsAppAccessRestricted} */
  const paymentIncidentArgs = {
    enabled: !isNil(token) && !isNil(org.id),
    orgId: org.id
  }

  const appRestriction = useIsAppAccessRestricted()
  const { hasPaymentIncident, paymentIncidentRequest } = useHasPaymentIncident(paymentIncidentArgs)

  const hasDowngradeWarn =
    !isNil(org) &&
    canUser(ops.edit, 'billing', { organization: org }) &&
    appRestriction.isRestricted &&
    appRestriction.reason === 'downgrade' &&
    !isNil(paymentIncidentRequest.data)

  return (
    <>
      {/* Warning Modal */}
      {hasDowngradeWarn && (
        <DowngradeWarn
          orgId={org.id}
          paymentDueDate={paymentIncidentRequest.data?.paymentDueDate}
        />
      )}

      {/* Payment incident Modal */}
      {hasPaymentIncident && (
        <Banner $feedback='error'>
          <SmartText
            tkey='ecommerce.billing.payment_incident.web'
            vars={{ orgId: org.id }}
            $color={colors.grayscale.white}
          />
        </Banner>
      )}

      <InvitNotificationPresentation />
      <ExternalConnectionsErrorMessage />

      {children}
    </>
  )
}

const AuthProviders: FC = ({ children }) => {
  const client = useClient()
  const { t } = useIntl()
  const [token] = useToken()
  const [loginAs] = useLoginAs()
  const pathname = usePathname()

  const config = {
    enabled: !isNil(token)
  }

  const meRequest = useQuery(client.me.get({ loginAsEmail: loginAs?.email }), config)
  const me = meRequest.data

  const myProfileRequest = useQuery(
    client.me.getMyProfile({ loginAsEmail: loginAs?.email }),
    config
  )
  const profile = myProfileRequest.data

  const { org, isLoading: orgIsLoading } = useDefaultOrg()

  const isLoading = orgIsLoading || myProfileRequest.isLoading || meRequest.isLoading

  const unAuthorized = meRequest.error?.status === 403 || myProfileRequest.error?.status === 403

  const errorOnUserFetching = meRequest.isError || myProfileRequest.isError

  const unableToGetOwnIdentity = errorOnUserFetching || unAuthorized

  if (unableToGetOwnIdentity) {
    // user is unauthorized to get is own identity or org so we should disconnect him
    clearAllStates(loginAs !== null)
  }

  return isLoading ? (
    <ModalOverlay>
      <Flex $flexDirection='column' $alignItems='center' $justifyContent='center' $gap='md'>
        <Flex
          $flexDirection='column'
          $alignItems='center'
          $justifyContent='center'
          $position='relative'
          $width={32}
          $height={32}
        >
          <div style={{ position: 'absolute' }}>
            <LoadingCircle size='2xl' color={'grayscale.black'} />
          </div>
          <LogoSphereDark width={24} height={24} />
        </Flex>
        <Text $textAlign='center' $fontWeight='bold'>
          {t('status.loading', { capitalize: true })}
        </Text>
      </Flex>
    </ModalOverlay>
  ) : !isNil(profile) && !isNil(me) && !isNil(org) ? (
    <UserContextProvider profile={profile} me={me}>
      <OrgContextProvider org={org}>
        {me.isEmailVerified === false && !pathname.includes('/verify-email') ? (
          <Redirect to='/verify-email/waiting' keepCurrentParams />
        ) : (
          <>
            <OnUserLoginBehavior />
            {children}
          </>
        )}
      </OrgContextProvider>
    </UserContextProvider>
  ) : !unableToGetOwnIdentity ? (
    // We use to render children here, but I don't see why we'd like to
    // If we don't have a profile or me or org, we should display an error screen
    // There is no point in attempting to render children that are aware of the context
    // If the context cannot be setup properly
    <ErrorScreen status={unAuthorized ? 403 : 500} />
  ) : null
}

export default AuthProviders
